import React, { useContext, useEffect, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { get } from 'lodash';
import { FormContext } from 'contexts';
import { v4 as uuid } from 'uuid';
import { Button } from 'components/common';
import {
  AuFormItemWrapper,
  AuErrorText
} from 'components/DTA';
import { fileData } from 'types/forms/arnApplicationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import { IDENTITY_TYPES } from 'arnConstants';

const IdentityFileUpload: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<Array<fileData>>([]);
  const { errors, getFieldMeta, setFieldValue } = useFormikContext();
  const { setFormAlert } = useContext(FormContext);
  const fieldError = get(errors, 'manualPOIDocuments.hasUploadedFiles');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFieldValue('manualPOIDocuments.isManualUpload', true);
    setFieldValue('identityType', IDENTITY_TYPES.MANUAL_UPLOAD);
    const fieldValue = getFieldMeta('manualPOIDocuments.identityFiles').value as fileData[];

    if (fieldValue) {
      setSelectedFiles(fieldValue);
    }
  }, []);

  useEffect(() => {
    setFieldValue('manualPOIDocuments.identityFiles', selectedFiles);
    const hasUploadedFile = selectedFiles && selectedFiles.length > 0;
    setFieldValue('manualPOIDocuments.hasUploadedFiles', hasUploadedFile);
  }, [selectedFiles]);

  const handleChooseFile = ({ currentTarget: { files } }: React.ChangeEvent<HTMLInputElement>) => {
    if (files) {
      const filenames = getFieldMeta('manualPOIDocuments.filename').value as string[];
      if ((filenames.length + files.length) > 10) {
        const alert: IFormAlertType = {
          as: 'error',
          title: 'There is a problem uploading your file',
          message: 'Maximum number of files exceeded. You can upload up to ten files per submission.'
        };
        setFormAlert(alert);
        return;
      }

      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          const fileSizeInMB = files[i].size / 1024 / 1024;
          //check for file size
          if (fileSizeInMB > 2) {
            const alert: IFormAlertType = {
              as: 'error',
              title: 'There is a problem uploading your file',
              message: 'One of the documents exceeds the maximum file size of 2MB'
            };
            setFormAlert(alert);
            window.scrollTo(0, 0);
            return;
          }
          //check file extrnsion
          const extension = files[i].name.split('.').pop();
          const fileExtension = extension?.toLowerCase();
          const allowedFileExtensions = ['jpg', 'jpeg', 'bmp', 'png', 'pdf'];
          const isAllowed = allowedFileExtensions.find(ext => (fileExtension === ext));
          if (!isAllowed) {
            const alert: IFormAlertType = {
              as: 'error',
              title: 'There is a problem uploading your file',
              message: <p>One of the uploaded files is not in the supported formats. Please check the files you&lsquo;re trying to upload.</p>
            };
            setFormAlert(alert);
            window.scrollTo(0, 0);
            return;
          }
          //check for already selected file
          const isAlreadySelected = selectedFiles.find(item => item.file.name === files[i].name);
          if (isAlreadySelected) {
            const alert: IFormAlertType = {
              as: 'error',
              title: 'There is a problem uploading your files',
              message: <>We can&apos;t accept files with the same name. Check the names of the files you are trying to upload.</>
            };
            setFormAlert(alert);
            window.scrollTo(0, 0);
            return;
          }
        }

        for (let i = 0; i < files.length; i++) {
          const id = uuid().toString();
          const url = window.URL.createObjectURL(files[i]);
          filenames.push(files[i].name);
          const file = { file: files[i], id, blodUrl: url };
          setSelectedFiles(existing => existing.concat(file));
        }
        setFieldValue('manualPOIDocuments.filename', filenames);
        setFormAlert(null);
      }
    }
  };

  const removeDocument = (id: uuid) => {
    const files = selectedFiles.filter(item => item.id !== id);
    setSelectedFiles(files);
    const filenames = files.map(file => file.file.name);
    setFieldValue('manualPOIDocuments.filename', filenames);
  };

  return (
    <>
      <AuFormItemWrapper
        className={
          'au-form-group ' +
          `${getFieldMeta('manualPOIDocuments.hasUploadedFiles').error ? '  au-form-group--invalid' : ''}`
        }>
        <Field
          name={'manualPOIDocuments.hasUploadedFiles'}
          className='au-control-input__input'
        >
          {() => (
            <div className={
              'au-control-input ' +
              `${getFieldMeta('manualPOIDocuments.hasUploadedFiles').error ? ' au-control-input--invalid' : ''}`
            }>
              <label>Upload documentation</label>
              <br />
              <input
                className='fileInput'
                type="file"
                name="file"
                multiple
                accept='.pdf,.jpg,.jpeg,.bmp,.png'
                onChange={handleChooseFile}
                ref={fileInputRef}
              />
              <Button as='secondary' onClick={
                () => {
                  fileInputRef.current?.click();
                }
              }>Choose file</Button>
              {
                fieldError &&
                <AuErrorText text={get(errors, 'manualPOIDocuments.hasUploadedFiles')} />
              }
              <p>Each uploaded file must be no more than 2mb in size.</p>
              <p>You can upload up up to ten files per submission. Accepted file types are PDF documents (.pdf) and the following image formats: .jpg, .jpeg, .bmp and .png</p>
            </div>
          )
          }</Field>
        <br />
        <div className='file-picker'>
          <div className="uploaded-files" id="uploaded-files">
            <ul>
              {
                selectedFiles.map(item =>
                  <li key={item.id}>
                    <a
                      target="_blank"
                      href={item.blodUrl}
                      rel="noopener noreferrer"
                      className="file-name"
                      aria-label={item.file.name}
                    >
                      {item.file.name}
                    </a>
                    <span
                      className="remove-file close"
                      role="button"
                      aria-label="Press Enter to delete the file {item.name}"
                      onClick={
                        () => {
                          removeDocument(item.id);
                        }
                      }>
                      x
                    </span>
                  </li>
                )
              }
            </ul>
          </div>
        </div>
      </AuFormItemWrapper>
    </>
  );
};

export default IdentityFileUpload;